/**
 * Точки «сыграно с нами» в овале «инфо о клиентах» (30.09, владелец): «точка — на ИГРУ, а не
 * на запись; две игры — две точки, пять — пять, ноль — ноль». Текущая бронь, будущие брони,
 * лиды и отмены не в счёт; игры в чужих командах (по вейверам) — в счёт. Считает сервер
 * (GET /api/clients/game-dots); бейджи экрана собираются в ОДИН запрос (пачка за 30 мс, по 90 —
 * лимит D1), ответ живёт 5 минут; открытый экран раз в минуту досчитывает устаревшие.
 * Нет доступа к карточке клиента — не спрашиваем и не рисуем.
 */
import { useEffect, useState } from "react";
import { api } from "../../api/client";
import { usePlayersAccess } from "./PlayersBadge";
import css from "./PlayersBadge.module.css";

const TTL_MS = 5 * 60_000;
const CHUNK = 90;
/** Больше — не влезает в овал; точное число — в подсказке бейджа. */
export const MAX_DOTS = 8;

const cache = new Map<number, { n: number; at: number }>();
const subs = new Map<number, Set<(n: number) => void>>();
let queued = new Set<number>();
let timer: ReturnType<typeof setTimeout> | null = null;
let ticker: ReturnType<typeof setInterval> | null = null;

function flush() {
  timer = null;
  const ids = [...queued];
  queued = new Set();
  for (let i = 0; i < ids.length; i += CHUNK) {
    const part = ids.slice(i, i + CHUNK);
    api<{ dots?: Record<string, number> }>(`/api/clients/game-dots?ids=${part.join(",")}`)
      .then((r) => {
        const now = Date.now();
        for (const id of part) {
          const n = Number(r.dots?.[id] ?? 0) || 0;
          cache.set(id, { n, at: now });
          subs.get(id)?.forEach((f) => f(n));
        }
      })
      .catch(() => { /* нет связи — без точек; спросим снова (показ или минутный досчёт) */ });
  }
}

function request(id: number) {
  queued.add(id);
  timer ??= setTimeout(flush, 30);
}

/** Раз в минуту — устаревшие точки видимых бейджей (сдали отчёт — у соседних игр клиента +1). */
function ensureTicker() {
  ticker ??= setInterval(() => {
    if (!subs.size) { clearInterval(ticker!); ticker = null; return; }
    const now = Date.now();
    for (const id of subs.keys()) if (now - (cache.get(id)?.at ?? 0) > TTL_MS) request(id);
  }, 60_000);
}

/** Сколько игр сыграно с клиентом этой брони (0 — пока неизвестно или истории нет). */
export function useGameDots(bookingId: number | null | undefined, enabled: boolean): number {
  const id = enabled && bookingId ? bookingId : 0;
  const [n, setN] = useState(() => (id ? cache.get(id)?.n ?? 0 : 0));
  useEffect(() => {
    if (!id) { setN(0); return; }
    const hit = cache.get(id);
    setN(hit?.n ?? 0);
    if (!hit || Date.now() - hit.at > TTL_MS) request(id);
    let set = subs.get(id);
    if (!set) subs.set(id, (set = new Set()));
    set.add(setN);
    ensureTicker();
    return () => {
      set.delete(setN);
      if (!set.size) subs.delete(id);
    };
  }, [id]);
  return n;
}

const byPhone = new Map<string, { n: number; at: number }>();
/** Новая бронь: номер набран целиком, клиент найден по номеру — сколько игр с ним сыграно. */
export function useGameDotsByPhone(phone: string | null | undefined, enabled: boolean): number {
  const digits = enabled ? String(phone ?? "").replace(/\D/g, "") : "";
  const key = digits.length === 11 ? digits : "";
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!key) { setN(0); return; }
    const hit = byPhone.get(key);
    if (hit && Date.now() - hit.at < TTL_MS) { setN(hit.n); return; }
    let alive = true;
    api<{ phone_games?: number }>(`/api/clients/game-dots?phone=${encodeURIComponent(key)}`)
      .then((r) => { const v = Number(r.phone_games) || 0; byPhone.set(key, { n: v, at: Date.now() }); if (alive) setN(v); })
      .catch(() => { if (alive) setN(0); });
    return () => { alive = false; };
  }, [key]);
  return n;
}

/** Сами точки (внутри овала). n ≤ 0 — ничего. */
export function GameDots({ n, max = MAX_DOTS }: { n: number; max?: number }) {
  if (n <= 0) return null;
  return (
    <span className={css.dots} aria-hidden="true">
      {Array.from({ length: Math.min(n, max) }, (_, i) => <i key={i} />)}
    </span>
  );
}

/** Для овала «Инфо» в окне брони: доступ проверяет сам; без брони (создание) — по номеру. */
export function GameDotsFor({ bookingId, phone, max }: { bookingId?: number | null; phone?: string | null; max?: number }) {
  const access = usePlayersAccess();
  const byId = useGameDots(bookingId, access);
  const byNum = useGameDotsByPhone(phone, access && !bookingId);
  return <GameDots n={bookingId ? byId : byNum} max={max} />;
}
